/**
 * CreateWorkflowDialog - Modal dialog for creating a new workflow.
 *
 * Uses radio-card selection: "Blank workflow" (default) plus featured
 * templates. Each card shows an icon, name, and brief description.
 * A radio indicator marks the active selection.
 *
 * Pre-fills a unique default name and validates input using the same rules
 * as LogicAppsPortal (regex, reserved words, duplicates).
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Field,
  Spinner,
  Text,
  MessageBar,
  MessageBarBody,
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  DismissRegular,
  ArrowRightRegular,
  FlowRegular,
  GlobeRegular,
  DatabaseRegular,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';
import { validateWorkflowName } from '@/utils/validation';
import { BUILTIN_MANIFESTS, FEATURED_TEMPLATE_IDS } from '@/data/templates';
import type { TemplateManifest } from '@/types/template';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { AutomationAppUIModel } from '@/types/automation';

/** Sentinel value for the "blank workflow" selection. */
const BLANK_TEMPLATE_ID = '__blank__';

/** Featured templates for the create dialog, unified with empty canvas via FEATURED_TEMPLATE_IDS. */
const FEATURED_TEMPLATES: TemplateManifest[] = FEATURED_TEMPLATE_IDS
  .map((id) => BUILTIN_MANIFESTS.find((t) => t.id === id))
  .filter((t): t is TemplateManifest => t !== undefined);

/** Per-template card metadata: icon, short label, and description. */
const CARD_META: Record<string, { icon: React.ReactNode; label: string; description: string }> = {
  [BLANK_TEMPLATE_ID]: {
    icon: <FlowRegular />,
    label: 'Blank workflow',
    description: 'Design your automation from scratch',
  },
  'tool-call-uri-http': {
    icon: <GlobeRegular />,
    label: 'HTTP Endpoint Caller',
    description: 'Call an HTTP endpoint and return the response',
  },
  'tool-execute-query-sql': {
    icon: <DatabaseRegular />,
    label: 'SQL Query Executor',
    description: 'Execute a SQL query and return the result set',
  },
  'try-catch': {
    icon: <ShieldCheckmarkRegular />,
    label: 'Try-Catch Error Handler',
    description: 'Catch and handle errors with structured Scope actions',
  },
};

const useStyles = makeStyles({
  surface: {
    maxWidth: '500px',
    width: '100%',
  },
  input: {
    width: '100%',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
  templateSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalL,
  },
  sectionLabel: {
    color: tokens.colorNeutralForeground3,
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalSNudge,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderWidth('1px'),
    ...shorthands.borderStyle('solid'),
    ...shorthands.borderColor(tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    outline: 'none',
    transitionProperty: 'background-color, border-color',
    transitionDuration: '0.15s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '1px',
    },
  },
  cardSelected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  cardDisabled: {
    opacity: '0.5',
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
  radio: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    ...shorthands.borderWidth('1.5px'),
    ...shorthands.borderStyle('solid'),
    ...shorthands.borderColor(tokens.colorNeutralStroke1),
    flexShrink: 0,
    marginTop: '2px',
    transitionProperty: 'border-color, background-color',
    transitionDuration: '0.15s',
  },
  radioSelected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandStroke1,
  },
  radioInner: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '18px',
    flexShrink: 0,
    marginTop: '1px',
  },
  cardIconSelected: {
    color: tokens.colorBrandForeground1,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: 0,
  },
  cardDescription: {
    color: tokens.colorNeutralForeground3,
  },
  browseRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  errorContainer: {
    marginBottom: tokens.spacingVerticalM,
  },
});

export type CreateWorkflowDialogProps = {
  open: boolean;
  defaultName: string;
  app: AutomationAppUIModel;
  isCreating: boolean;
  /** Error message from mutation failure */
  error?: string | null;
  onSubmit: (name: string) => void;
  onCancel: () => void;
  /** Called when user wants to browse the full template gallery. */
  onBrowseTemplates?: () => void;
  /** Called when user picks a template card — creates the workflow and applies the template. */
  onCreateFromTemplate?: (workflowName: string, templateId: string) => void;
};

export const CreateWorkflowDialog = ({
  open,
  defaultName,
  app,
  isCreating,
  error,
  onSubmit,
  onCancel,
  onBrowseTemplates,
  onCreateFromTemplate,
}: CreateWorkflowDialogProps) => {
  const styles = useStyles();
  const [name, setName] = useState(defaultName);
  const [validation, setValidation] = useState<{ isValid: boolean; error?: string }>({ isValid: true });
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(BLANK_TEMPLATE_ID);
  const { data: workflowsInApp } = useAppWorkflows(app);
  const existingNames = useMemo(() => workflowsInApp?.map(wf => wf.name) ?? [], [workflowsInApp]);

  // Reset name and selection when dialog opens with a new default
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setValidation({ isValid: true });
      setSelectedTemplateId(BLANK_TEMPLATE_ID);
    }
  }, [open, defaultName]);

  const handleNameChange = useCallback(
    (_: unknown, data: { value: string }) => {
      const val = data.value;
      setName(val);
      if (!val.trim()) {
        setValidation({ isValid: false, error: 'Workflow name cannot be empty' });
      } else {
        setValidation(validateWorkflowName(val, existingNames));
      }
    },
    [existingNames]
  );

  const handleSubmit = useCallback(() => {
    const result = validateWorkflowName(name, existingNames);
    if (!result.isValid) {
      setValidation(result);
      return;
    }
    const normalizedName = result.normalizedName ?? name.trim();
    if (selectedTemplateId !== BLANK_TEMPLATE_ID && onCreateFromTemplate) {
      onCreateFromTemplate(normalizedName, selectedTemplateId);
    } else {
      onSubmit(normalizedName);
    }
  }, [name, existingNames, selectedTemplateId, onSubmit, onCreateFromTemplate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && validation.isValid && !isCreating) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [validation.isValid, isCreating, handleSubmit]
  );

  const handleCardSelect = useCallback(
    (templateId: string) => {
      if (isCreating) return;
      setSelectedTemplateId(templateId);
    },
    [isCreating]
  );

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent, templateId: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCardSelect(templateId);
      }
    },
    [handleCardSelect]
  );

  const showTemplateSection = FEATURED_TEMPLATES.length > 0 && onCreateFromTemplate !== undefined;

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onCancel(); }}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={onCancel}
                aria-label="Close"
                disabled={isCreating}
              />
            }
          >
            Create workflow
          </DialogTitle>
          <DialogContent>
            {error && (
              <div className={styles.errorContainer}>
                <MessageBar intent="error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              </div>
            )}
            <Field
              label="Workflow name"
              required
              validationMessage={validation.error ?? null}
              validationState={validation.error ? 'error' : 'none'}
            >
              <Input
                className={styles.input}
                value={name}
                onChange={handleNameChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter a name for the new workflow"
                disabled={isCreating}
                autoFocus
              />
            </Field>

            {showTemplateSection && (
              <div className={styles.templateSection}>
                <Text size={200} weight="semibold" className={styles.sectionLabel}>
                  Template
                </Text>
                <div className={styles.cardList} role="radiogroup" aria-label="Workflow template">
                  <TemplateCard
                    id={BLANK_TEMPLATE_ID}
                    isSelected={selectedTemplateId === BLANK_TEMPLATE_ID}
                    isDisabled={isCreating}
                    styles={styles}
                    onSelect={handleCardSelect}
                    onKeyDown={handleCardKeyDown}
                  />
                  {FEATURED_TEMPLATES.map((template) => (
                    <TemplateCard
                      key={template.id}
                      id={template.id}
                      isSelected={selectedTemplateId === template.id}
                      isDisabled={isCreating}
                      styles={styles}
                      onSelect={handleCardSelect}
                      onKeyDown={handleCardKeyDown}
                    />
                  ))}
                </div>
                {onBrowseTemplates && (
                  <div className={styles.browseRow}>
                    <Button
                      appearance="transparent"
                      icon={<ArrowRightRegular />}
                      iconPosition="after"
                      size="small"
                      onClick={onBrowseTemplates}
                      disabled={isCreating}
                    >
                      Browse all templates
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button appearance="secondary" onClick={onCancel} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={!validation.isValid || !name.trim() || isCreating}
              icon={isCreating ? <Spinner size="tiny" /> : null}
            >
              {isCreating ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

// =============================================================================
// TemplateCard — radio-style card with icon, name + description
// =============================================================================

interface TemplateCardProps {
  id: string;
  isSelected: boolean;
  isDisabled: boolean;
  styles: ReturnType<typeof useStyles>;
  onSelect: (id: string) => void;
  onKeyDown: (e: React.KeyboardEvent, id: string) => void;
}

const TemplateCard = ({
  id,
  isSelected,
  isDisabled,
  styles,
  onSelect,
  onKeyDown,
}: TemplateCardProps) => {
  const meta = CARD_META[id];
  if (!meta) return null;

  return (
    <div
      className={mergeClasses(
        styles.card,
        isSelected && styles.cardSelected,
        isDisabled && styles.cardDisabled,
      )}
      role="radio"
      tabIndex={isDisabled ? -1 : 0}
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      aria-label={id === BLANK_TEMPLATE_ID ? 'Blank workflow' : `Template: ${meta.label}`}
      onClick={() => onSelect(id)}
      onKeyDown={(e) => onKeyDown(e, id)}
    >
      <span className={mergeClasses(styles.radio, isSelected && styles.radioSelected)}>
        {isSelected && <span className={styles.radioInner} />}
      </span>
      <span className={mergeClasses(styles.cardIcon, isSelected && styles.cardIconSelected)}>
        {meta.icon}
      </span>
      <span className={styles.cardContent}>
        <Text size={200} weight="semibold" truncate wrap={false}>
          {meta.label}
        </Text>
        <Text size={100} className={styles.cardDescription} truncate wrap={false}>
          {meta.description}
        </Text>
      </span>
    </div>
  );
};
